import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { format } from "d3-format";
import { timeFormat } from "d3-time-format";
import { scaleTime } from "d3-scale";
import { curveMonotoneX } from "d3-shape";

import { ChartCanvas, Chart } from "react-stockcharts";
import { AreaSeries } from "react-stockcharts/lib/series";
import { XAxis, YAxis } from "react-stockcharts/lib/axes";
import { discontinuousTimeScaleProvider } from "react-stockcharts/lib/scale";
import { last } from "react-stockcharts/lib/utils";

import { fitWidth } from "react-stockcharts/lib/helper";
import {
  createVerticalLinearGradient,
  hexToRGBA,
} from "react-stockcharts/lib/utils";
import CrossHairCursor from "react-stockcharts/lib/coordinates/CrossHairCursor";
import MouseCoordinateY from "react-stockcharts/lib/coordinates/MouseCoordinateY";
import OHLCTooltip from "react-stockcharts/lib/tooltip/OHLCTooltip";

const canvasGradient = createVerticalLinearGradient([
  { stop: 0, color: hexToRGBA("#b5d0ff", 0.2) },
  { stop: 0.7, color: hexToRGBA("#6fa4fc", 0.4) },
  { stop: 1, color: hexToRGBA("#4286f4", 0.8) },
]);

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
}
let AreaChart = (props) => {
  const { type, width, ratio } = props;

  const initialData = props.data;
  const xScaleProvider = discontinuousTimeScaleProvider.inputDateAccessor(
    (d) => d.date
  );
  const { data, xScale, xAccessor, displayXAccessor } =
    xScaleProvider(initialData);

  const start = xAccessor(last(data));
  const end = xAccessor(data[Math.max(0, data.length - 150)]);
  const xExtents = [start, end];

  const [windowDimensions, setWindowDimensions] = useState(
    getWindowDimensions()
  );

  useEffect(() => {
    const handleResize = () => {
      setWindowDimensions(getWindowDimensions());
    };
    handleResize();
  }, []);

  const height = windowDimensions.height < 800 ? 640 : 850;

  return (
    <ChartCanvas
      ratio={ratio}
      width={width}
      height={height}
      margin={{ left: 80, right: 55, top: 10, bottom: 50 }}
      seriesName="MSFT"
      data={data}
      type={type}
      xScale={xScale}
      xAccessor={xAccessor}
      displayXAccessor={displayXAccessor}
      xExtents={xExtents}
    >
      <Chart id={0} yExtents={(d) => d.close}>
        <defs>
          <linearGradient id="MyGradient" x1="0" y1="100%" x2="0" y2="0%">
            <stop offset="0%" stopColor="#b5d0ff" stopOpacity={0.2} />
            <stop offset="70%" stopColor="#6fa4fc" stopOpacity={0.4} />
            <stop offset="100%" stopColor="#4286f4" stopOpacity={0.8} />
          </linearGradient>
        </defs>
        <XAxis axisAt="bottom" orient="bottom" ticks={6} />
        <YAxis axisAt="right" orient="right" />
        <AreaSeries
          yAccessor={(d) => d.close}
          fill="url(#MyGradient)"
          strokeWidth={2}
          interpolation={curveMonotoneX}
          canvasGradient={canvasGradient}
        />
        <MouseCoordinateY
          at="right"
          orient="right"
          displayFormat={format(".2f")}
        />

        <OHLCTooltip
          labelFill="#ffffff"
          textFill="#ffffff"
          origin={[-40, 30]}
        />
      </Chart>
      <CrossHairCursor
        opacity="0.6"
        stroke="#ffffff"
        strokeDasharray="LongDashDot"
      />
    </ChartCanvas>
  );
};

AreaChart.propTypes = {
  data: PropTypes.array.isRequired,
  width: PropTypes.number.isRequired,
  ratio: PropTypes.number.isRequired,
  type: PropTypes.oneOf(["svg", "hybrid"]).isRequired,
};

AreaChart.defaultProps = {
  type: "svg",
};
AreaChart = fitWidth(AreaChart);

export default AreaChart;
